import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { LightboxComponent } from '../../shared/components/lightbox/lightbox.component';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
@Component({
  selector: 'app-project-detail',
  standalone: true,
  imports: [RouterLink, ScrollAnimateDirective, LightboxComponent],
  template: `
    @if (project) {
      <!-- Hero -->
      <section class="relative min-h-[420px] md:min-h-[520px] flex items-end -mt-20 overflow-hidden">
        <img [src]="project.image" [alt]="project.title" class="absolute inset-0 w-full h-full object-cover" />
        <div class="absolute inset-0 bg-gradient-to-t from-surface-950/90 via-surface-950/40 to-surface-950/20"></div>
        <div class="relative z-10 w-full pb-12 pt-40 md:pt-48">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <a routerLink="/projects" class="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-8 transition-colors">
              <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
              Back to Projects
            </a>
            <span class="inline-block px-3 py-1 rounded-md bg-brand-500/20 text-brand-300 text-[0.65rem] font-bold uppercase tracking-wider mb-4">{{ project.category }}</span>
            <h1 class="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white leading-[1.1] tracking-tight mb-4">{{ project.title }}</h1>
            <p class="text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">{{ project.description }}</p>
          </div>
        </div>
      </section>

      <!-- Metadata Bar -->
      <section class="bg-white border-b border-surface-100 shadow-sm">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
          <div class="flex flex-wrap items-center gap-x-10 gap-y-4 py-5">
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                <svg aria-hidden="true" class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
              </div>
              <div>
                <div class="text-[0.6rem] font-semibold uppercase tracking-wider text-surface-400">Location</div>
                <div class="text-sm font-medium text-surface-900">{{ project.location }}</div>
              </div>
            </div>
            <div class="flex items-center gap-3">
              <div class="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center">
                <svg aria-hidden="true" class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" /><path stroke-linecap="round" stroke-linejoin="round" d="M6 6h.008v.008H6V6z" /></svg>
              </div>
              <div>
                <div class="text-[0.6rem] font-semibold uppercase tracking-wider text-surface-400">Category</div>
                <div class="text-sm font-medium text-surface-900">{{ project.category }}</div>
              </div>
            </div>
            <div class="ml-auto hidden md:block">
              <a routerLink="/contact" class="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white font-semibold text-sm rounded-lg hover:bg-brand-700 transition-colors">
                Get a Similar Quote
                <svg aria-hidden="true" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      <!-- Details -->
      @if (project.details && project.details.length) {
        <section class="py-16 md:py-20 bg-white">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="max-w-3xl">
              <div class="flex items-center gap-3 mb-8">
                <div class="w-1 h-8 rounded-full bg-brand-500"></div>
                <h2 class="font-heading text-xl md:text-2xl font-bold text-surface-900">Project Highlights</h2>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                @for (detail of project.details; track $index; let i = $index) {
                  <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i+1)*80"
                       class="flex items-start gap-3 p-4 rounded-xl bg-surface-50 border border-surface-100">
                    <div class="w-6 h-6 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center shrink-0 mt-0.5">
                      <svg aria-hidden="true" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span class="text-sm text-surface-700 leading-relaxed">{{ detail }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        </section>
      }

      <!-- Gallery -->
      @if (project.gallery && project.gallery.length) {
        <section class="py-16 md:py-20 bg-surface-50 border-t border-surface-100">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="flex items-center gap-3 mb-10">
              <div class="w-1 h-8 rounded-full bg-brand-500"></div>
              <h2 class="font-heading text-xl md:text-2xl font-bold text-surface-900">Project Gallery</h2>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              @for (img of project.gallery; track $index; let i = $index) {
                <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i+1)*100"
                     (click)="lightbox.open(i)"
                     class="group aspect-[4/3] rounded-xl overflow-hidden relative cursor-pointer">
                  <img [src]="img" [alt]="project.title + ' - Image ' + (i + 1)" class="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
                  <div class="absolute inset-0 bg-surface-950/0 group-hover:bg-surface-950/20 transition-colors duration-300 flex items-center justify-center">
                    <svg aria-hidden="true" class="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" /></svg>
                  </div>
                </div>
              }
            </div>
            <app-lightbox [images]="project.gallery!" [alt]="project.title" />
          </div>
        </section>
      }

      <!-- Related Projects -->
      @if (relatedProjects.length) {
        <section class="py-16 md:py-20 bg-white border-t border-surface-100">
          <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="flex items-center justify-between mb-10">
              <div class="flex items-center gap-3">
                <div class="w-1 h-8 rounded-full bg-brand-500"></div>
                <h2 class="font-heading text-xl md:text-2xl font-bold text-surface-900">Related Projects</h2>
              </div>
              <a routerLink="/projects" class="group inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors">
                View All
                <svg aria-hidden="true" class="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              @for (rp of relatedProjects; track rp.slug) {
                <a [routerLink]="'/projects/' + rp.slug"
                   class="group rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-xl hover:shadow-brand-600/[0.08] transition-all duration-400 hover:-translate-y-1">
                  <div class="aspect-[4/3] overflow-hidden relative">
                    <img [src]="rp.image" [alt]="rp.title" class="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500" />
                    <div class="absolute inset-0 bg-gradient-to-t from-surface-950/60 via-surface-950/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
                    <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-400">
                      <span class="inline-flex items-center gap-2 px-5 py-2.5 border border-white/80 text-white text-sm font-semibold rounded-lg backdrop-blur-sm bg-white/10 translate-y-3 group-hover:translate-y-0 transition-transform duration-400">
                        View Project
                      </span>
                    </div>
                  </div>
                  <div class="p-5">
                    <span class="text-[0.65rem] font-bold uppercase tracking-wider text-brand-600">{{ rp.category }}</span>
                    <h3 class="font-heading text-base font-bold text-surface-900 mt-1 group-hover:text-brand-700 transition-colors">{{ rp.title }}</h3>
                  </div>
                </a>
              }
            </div>
          </div>
        </section>
      }

      <!-- CTA -->
      <section class="py-16 md:py-20 bg-brand-600 relative overflow-hidden">
        <div class="absolute inset-0 bg-gradient-to-br from-brand-700 via-brand-600 to-brand-500"></div>
        <div class="mx-auto max-w-4xl px-6 lg:px-8 text-center relative z-10">
          <h2 class="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Want Results Like These?</h2>
          <p class="text-brand-100 mb-8 max-w-lg mx-auto">Let's discuss your project. Get a free, no-obligation estimate today.</p>
          <div class="flex flex-wrap items-center justify-center gap-4">
            <a routerLink="/contact" class="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-700 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-colors shadow-lg shadow-brand-900/20">
              Get Free Estimate
              <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
            <a routerLink="/projects" class="inline-flex items-center gap-2 px-7 py-3.5 border border-white/25 text-white font-semibold text-sm rounded-lg hover:bg-white/10 transition-colors">
              Browse More Projects
            </a>
          </div>
        </div>
      </section>
    } @else {
      <section class="py-32 text-center -mt-20 pt-52">
        <div class="mx-auto max-w-md px-6">
          <div class="w-16 h-16 rounded-full bg-surface-100 text-surface-400 flex items-center justify-center mx-auto mb-6">
            <svg aria-hidden="true" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          </div>
          <h1 class="font-heading text-2xl font-bold text-surface-900 mb-3">Project Not Found</h1>
          <p class="text-surface-500 mb-6">The project you're looking for doesn't exist or may have been moved.</p>
          <a routerLink="/projects" class="inline-flex items-center gap-2 px-6 py-3 bg-brand-600 text-white font-semibold text-sm rounded-lg hover:bg-brand-700 transition-colors">
            <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" /></svg>
            Browse All Projects
          </a>
        </div>
      </section>
    }
  `,
})
export class ProjectDetailComponent implements OnInit {
  @ViewChild(LightboxComponent) lightbox!: LightboxComponent;

  private route = inject(ActivatedRoute);
  private seo = inject(SeoService);
  private config = inject(SiteConfigService);

  project: any;
  relatedProjects: any[] = [];

  ngOnInit(): void {
    const slug = this.route.snapshot.paramMap.get('slug');
    const allProjects = this.config.pages.projects.projects;
    this.project = allProjects.find(p => p.slug === slug);

    if (this.project) {
      this.seo.setTitle(this.project.title);
      this.seo.setDescription(this.project.description);
      this.seo.setJsonLd(this.seo.getBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Projects', url: '/projects' },
        { name: this.project.title, url: '/projects/' + this.project.slug },
      ]));

      // Get up to 3 related projects from same category
      this.relatedProjects = allProjects
        .filter(p => p.slug !== slug && p.category === this.project.category)
        .slice(0, 3);

      // If not enough from same category, fill with others
      if (this.relatedProjects.length < 3) {
        const remaining = allProjects
          .filter(p => p.slug !== slug && !this.relatedProjects.includes(p))
          .slice(0, 3 - this.relatedProjects.length);
        this.relatedProjects = [...this.relatedProjects, ...remaining];
      }
    }
  }
}
